import { Box, Tooltip, Typography } from '@mui/material';
import { Info } from 'lucide-react';
import { useIntl } from 'react-intl';
import type { QueueEntry } from '../model';
import QueueNest, { type QueueListProps } from './QueueNest';
import { capSx } from './railStyles';

/** The drop's running tally at the foot of the rail. */
function QueueCount({ entries }: { entries: readonly QueueEntry[] }) {
    const intl = useIntl();
    const doneCount = entries.filter(
        (e) => e.status.kind === 'imported'
    ).length;
    const skippedCount = entries.filter(
        (e) => e.status.kind === 'skipped'
    ).length;
    return (
        <Typography
            sx={{ px: 1.5, pb: 1, fontSize: 11, color: 'text.disabled' }}
        >
            {intl.formatMessage(
                { id: 'patientList.import.queue.progress' },
                {
                    done: doneCount,
                    total: entries.length,
                    // The tally is inked brighter than the rest of the line.
                    tally: (chunks) => (
                        <Box component="span" sx={{ color: 'text.secondary' }}>
                            {chunks}
                        </Box>
                    ),
                }
            )}
            {skippedCount > 0
                ? intl.formatMessage(
                      { id: 'patientList.import.queue.skippedSuffix' },
                      { count: skippedCount }
                  )
                : ''}
        </Typography>
    );
}

/** The dashed catch-all drop target that sits at the foot of the rail. */
function DropAnywhere({ label }: { label: string }) {
    return (
        <Box
            sx={{
                mx: 0,
                border: 1,
                borderStyle: 'dashed',
                borderColor: 'divider',
                borderRadius: 1,
                p: 1,
                fontSize: 11,
                lineHeight: 1.5,
                color: 'text.disabled',
                textAlign: 'center',
            }}
        >
            {label}
        </Box>
    );
}

/**
 * What the scan threw away. The tally stays on screen because withheld files
 * must be reported, not merely discoverable; only the per-reason breakdown is
 * deferred to the tooltip. It counts the whole drop, not one patient, so it
 * belongs beside the drop's own file count and nowhere else.
 */
function SkippedHint({ count, summary }: { count: number; summary: string }) {
    const intl = useIntl();
    return (
        <Tooltip title={summary}>
            <Box
                component="span"
                tabIndex={0}
                aria-label={summary}
                sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 0.25,
                    flexShrink: 0,
                    color: 'text.secondary',
                }}
            >
                <Typography sx={{ fontSize: 11, color: 'inherit' }} noWrap>
                    {intl.formatMessage(
                        { id: 'patientList.import.source.skipped' },
                        { count }
                    )}
                </Typography>
                <Info size={12} />
            </Box>
        </Tooltip>
    );
}

type ImportQueueRailProps = {
    /** What the drop amounts to so far: its file count, or what the scan is doing. */
    diskSubtitle: string;
    /** The drop's skipped-file tally and its reasons, when the scan rejected anything. */
    skipped?: { count: number; summary: string };
    queue?: QueueListProps;
};

/**
 * The dialog's left column while files from this computer are being reviewed: what was dropped, the
 * patients it holds, and how far the review has got. A further drop replaces the queue, and the rail
 * says so for as long as there is one to replace.
 */
export default function ImportQueueRail({
    diskSubtitle,
    skipped,
    queue,
}: ImportQueueRailProps) {
    const intl = useIntl();
    return (
        <Box
            sx={{
                flex: 'none',
                width: 240,
                borderRight: 1,
                borderColor: 'divider',
                display: 'flex',
                flexDirection: 'column',
                py: 2,
                px: 1.5,
                gap: 0.25,
            }}
        >
            <Typography sx={capSx}>
                {intl.formatMessage({
                    id: 'patientList.import.source.thisComputer',
                })}
            </Typography>
            <Box
                sx={{
                    px: 1.5,
                    pb: 1,
                    display: 'flex',
                    flexDirection: 'column',
                }}
            >
                <Typography
                    sx={{ fontSize: 11, color: 'text.secondary' }}
                    noWrap
                >
                    {diskSubtitle}
                </Typography>
                {skipped && (
                    <SkippedHint
                        count={skipped.count}
                        summary={skipped.summary}
                    />
                )}
            </Box>
            {queue && <QueueNest {...queue} />}

            <Box sx={{ flex: 1 }} />
            {queue && <QueueCount entries={queue.entries} />}
            <DropAnywhere
                label={intl.formatMessage({
                    id: queue
                        ? 'patientList.import.dropAnywhereReplace'
                        : 'patientList.import.dropAnywhere',
                })}
            />
        </Box>
    );
}
